import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    forwardRef,
    useImperativeHandle,
} from "react";
import InfiniteScroll from "react-infinite-scroller";
import { Tabs, Spin } from "antd";
const { TabPane } = Tabs;
import debounce from "lodash/debounce";
import locale from "../../../_util/locale";
import CommonList from "../CommonList";
import searchTipsPng from "../assets/imgs/search-tips.png";
import noSearchResultPng from "../assets/imgs/no-search-result.png";

import { IItemData } from "../common.typing";
import { SearchTabInfo, SEARCH_STATUS } from "../utils";
import GroupMember from "../GroupMember";

const initialGroupInfo = {
    id: 0,
    name: "",
};

export interface ISearchPageProps {
    isSearchMode: boolean;
    setSearchMode: (val: boolean) => void;
    searchType?: string[];
    loadSearchData?: (key: string, rows?: number) => Promise<any>;
    currentList: IItemData[];
    setCurrentList: (list: IItemData[]) => void;
    /** 不可用 id，包括 group、user、session、org */
    disabledids?: string[];
    requiredList?: IItemData[];
    multiple?: boolean;
    loadSearchDataMore?: (
        type: string,
        key: string,
        rows?: number
    ) => Promise<any>;
    searchPlaceholder?: string;
    searchKey: string;
    setSearchKey: (key: string) => void;
    /** 埋点方法 */
    track: (eventName: string, eventData?: any) => void;
    title?: string;
    type?: string;
    /** 根据群 id 获取群成员 */
    getGroupMemberList?: (id: number | string) => void;
    /** 群成员列表 */
    groupMemberList?: IItemData[];
    /** 群成员分组 A-Z 分组数据）*/
    groupMemberGrouped?: { [letter: string]: IItemData[] };
    /** 隐藏群组成员页面标题 */
    isHideGroupMemberPageTitle?: boolean;
    /** 是否展示群组成员角色 */
    isShowGroupMemberRole?: boolean;
    /** 是否展示群组成员全选 */
    showGroupMemberCheckAll?: boolean;
}

const SearchPage = forwardRef<any, ISearchPageProps>(
    (
        {
            isSearchMode,
            setSearchMode,
            searchType,
            loadSearchData,
            currentList,
            setCurrentList,
            disabledids,
            requiredList,
            multiple,
            loadSearchDataMore,
            searchPlaceholder,
            searchKey,
            setSearchKey,
            track,
            title,
            type,
            getGroupMemberList,
            groupMemberList = [],
            groupMemberGrouped,
            isHideGroupMemberPageTitle,
            isShowGroupMemberRole,
            showGroupMemberCheckAll,
        },
        ref
    ) => {
        const searchKeyRef = useRef("");
        const [searchList, setSearchList] = useState<IItemData[]>([]); // 搜索结果集合
        const [searchStatus, setSearchStatus] = useState(SEARCH_STATUS.INIT); // 搜索状态
        const isDisabledSelect = !(searchType && searchType.length);
        const [userIsHasMore, setUserIsHasMore] = useState(false);
        const [groupIsHasMore, setGroupIsHasMore] = useState(false);

        const [userList, setUserList] = useState<IItemData[]>([]); // 用户list
        const [groupList, setGroupList] = useState<IItemData[]>([]); // 群聊list
        const [activeTabKey, setActiveTabKey] = useState(searchType?.[0]); // 设置当前激活的tab
        const [searchChildPage, setSearchChildPage] = useState(""); // 搜索结果的子页面 groupMember(群成员)
        const [currentGroupInfo, setCurrentGroupInfo] = useState<{
            id: number | string;
            name: string;
        }>(initialGroupInfo);

        useImperativeHandle(ref, () => ({
            onChange,
            clearKeyword,
            handleClose,
        }));

        // 清除子页面配置
        const clearChildPageInfo = () => {
            console.log("clearChildPageInfo");
            setSearchChildPage("");
            setCurrentGroupInfo(initialGroupInfo);
        };
        // 搜索关键字变化
        const tabKeys = useMemo(() => {
        return (searchType || []).map(t => SearchTabInfo[t].key);
        }, [searchType]);

        useEffect(() => {
            window.addEventListener("keydown", handleKeydown);
            return () => {
                window.removeEventListener("keydown", handleKeydown);
            };
        }, [activeTabKey]);
        

        const handleKeydown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();

                if (searchKeyRef.current) {
                    clearKeyword();
                } else {
                    handleClose();
                }
            }
            if(event.key === "Tab" ) {
                event.preventDefault();
                event.stopPropagation();
                if (!tabKeys.length) return;
                const cur = activeTabKey ?? tabKeys[0];
                const idx = tabKeys.indexOf(cur);
                const curIdx = idx >= 0 ? idx : 0;

                const dir = event.shiftKey ? -1 : 1;
                const nextIdx = (curIdx + dir + tabKeys.length) % tabKeys.length; // 实现首尾循环

                setActiveTabKey(tabKeys[nextIdx]);
            }
        };

        const handleClose = () => {
            setSearchKey("");
            setSearchMode(false);
            clearChildPageInfo();
        };

        // 清空关键词
        const clearKeyword = () => {
            setSearchKey("");
            searchKeyRef.current = "";
            setSearchList([]);
            setSearchStatus(SEARCH_STATUS.INIT);
        };

        // 关键字变化
        const onChange = (event: any) => {
            clearChildPageInfo();
            if (isDisabledSelect) return;
            if (!event || !event.target) return;

            const _keyword = event.target.value;
            setSearchList([]);
            setSearchKey(_keyword);

            searchKeyRef.current = _keyword;
            // 仅空格时不发起搜索（与空输入一致）
            if (!_keyword.trim() || !loadSearchData) {
                setSearchStatus(SEARCH_STATUS.INIT);
                return;
            }
            setSearchStatus(SEARCH_STATUS.WAIT);
            onSearch(_keyword);
        };

        const onSearch = useCallback(
            debounce(async key => {
                console.log("onSearch", key);
                if (!searchKeyRef.current.trim()) return;
                setSearchStatus(SEARCH_STATUS.SEARCHING);
                const result = loadSearchData && (await loadSearchData(key));
                if (result[0]?.key === searchKeyRef.current) {
                    setSearchList(result);
                    // setActiveTabKey(
                    //     // 自动定位到第一个有数据的tab
                    //     (result.find(i => i.data.length) || result[0])?.type
                    // );
                    setSearchStatus(SEARCH_STATUS.DONE);
                } else {
                    console.log(
                        "不是本次搜索结果，需要舍弃，不要进行任何操作，也不要重置当前搜索状态",
                        result[0]?.key,
                        searchKeyRef.current
                    );
                }
            }, 800),
            [loadSearchData]
        );

        // 搜索触发不止input输入一个来源，手动改变关键词也需要触发
        // useEffect(() => {
        //     searchKeyRef.current = searchKey;
        //     if (searchKey && loadSearchData) {
        //         setSearchStatus(SEARCH_STATUS.WAIT);
        //         onSearch(searchKeyRef.current);
        //     } else {
        //         setSearchStatus(SEARCH_STATUS.INIT);
        //     }
        // }, [searchKey]);

        const handleLoadSearchDataMore = async (type: string) => {
            if (!loadSearchDataMore) return;
            const result = await loadSearchDataMore(type, searchKeyRef.current);
            if (result[0]?.key === searchKeyRef.current) {
                setSearchList(result);
            } else {
                console.log(
                    "不是本次搜索结果，需要舍弃，不要进行任何操作",
                    result[0]?.key,
                    searchKeyRef.current
                );
            }
        };

        useEffect(() => {
            console.log("searchList", searchList);
            const userData = searchList.find(i => i.type === "user");
            const groupData = searchList.find(i => i.type === "group");
            setUserList(userData?.data || []);
            setGroupList(groupData?.data || []);
            setUserIsHasMore(userData?.isHasMore || false);
            setGroupIsHasMore(groupData?.isHasMore || false);
        }, [searchList]);

        // 自定义tab头部
        const renderTabBar: any = (props, DefaultTabBar) => (
            <div className="customTabBar">
                <DefaultTabBar {...props} className="site-custom-tab-bar" />
            </div>
        );

        // 请输入想要搜索的关键词
        const NoSearch = useMemo(() => {
            return (
                <div className="noSearchBox">
                    <img className="searchTipsImg" src={searchTipsPng} />
                    <div className="searchTipsText">
                        {locale("search_tips_text")}
                    </div>
                </div>
            );
        }, []);

        const Loading = useMemo(() => {
            return (
                <div className="loadingBox">
                    <Spin tip={`${locale("search_loading_text")}...`} />
                </div>
            );
        }, []);

        const onClickPartner = async (id: number | string, name: string) => {
            setCurrentGroupInfo({ id, name });
            setSearchChildPage("groupMember");
        };

        // 没有搜索结果
        const NoSearchResult = useMemo(() => {
            const parts = locale("search_no_result_text").split("[xxx]");
            const searchTipsText = (
                <>
                    {parts[0]}
                    <p className="keywordText">{searchKey}</p>
                    {parts[1]}
                </>
            );
            return (
                <div className="noResultBox">
                    <img className="searchTipsImg" src={noSearchResultPng} />
                    <div className="searchTipsText">{searchTipsText}</div>
                </div>
            );
        }, [searchKey]);

        // 搜索结果的子页面
        const searchChildPageContent = () => {
            let content: any = null;
            switch (searchChildPage) {
                case "groupMember":
                    content = (
                        <div className="groupMemberContent">
                            <GroupMember
                                {...{
                                    type,
                                    prePage: "",
                                    onChangeCurPage: (page: string) =>
                                        setSearchChildPage(page),
                                    currentList,
                                    setCurrentList,
                                    getGroupMemberList,
                                    groupMemberList,
                                    groupMemberGrouped,
                                    currentGroupInfo,
                                    disabledids,
                                    requiredList,
                                    isHideGroupMemberPageTitle,
                                    isShowGroupMemberRole,
                                    showCheckAll: showGroupMemberCheckAll,
                                    multiple,
                                }}
                            />
                        </div>
                    );
                    break;

                default:
                    break;
            }
            return <div className="searchChildPageContent">{content}</div>;
        };

        // 搜索结果的主页面
        const searchContent = () => {
            return (
                <div
                    className={`serchContent ${searchChildPage ? "hide" : ""}`}
                >
                    {searchStatus === SEARCH_STATUS.INIT && NoSearch}
                    {searchStatus === SEARCH_STATUS.WAIT && null}
                    {searchStatus === SEARCH_STATUS.SEARCHING && Loading}
                    {searchStatus === SEARCH_STATUS.DONE &&
                        (userList.length > 0 || groupList.length > 0 ? (
                            <Tabs
                                animated
                                defaultActiveKey={searchType?.[0]}
                                activeKey={activeTabKey}
                                onTabClick={tabKey => setActiveTabKey(tabKey)}
                                renderTabBar={renderTabBar}
                            >
                                {searchType?.map(tab => (
                                    <TabPane
                                        tab={SearchTabInfo[tab].tab_name}
                                        key={SearchTabInfo[tab].key}
                                    >
                                        {tab === "user" &&
                                            (userList.length > 0 ? (
                                                <div className="scrollWrapper">
                                                    <InfiniteScroll
                                                        initialLoad={false}
                                                        loadMore={() => {
                                                            handleLoadSearchDataMore(
                                                                tab
                                                            );
                                                        }}
                                                        hasMore={userIsHasMore}
                                                        useWindow={false}
                                                    >
                                                        <CommonList
                                                            {...{
                                                                list: userList,
                                                                currentList,
                                                                setCurrentList,
                                                                disabledids,
                                                                requiredList,
                                                                multiple,
                                                                isSearchHighlight:
                                                                    true,
                                                                track,
                                                                trackTitle:
                                                                    title,
                                                                isNeedTrack:
                                                                    true,
                                                                isFromSearch:
                                                                    true,
                                                                searchKey:
                                                                    searchKey,
                                                                searchType: tab,
                                                            }}
                                                        />
                                                    </InfiniteScroll>
                                                </div>
                                            ) : (
                                                NoSearchResult
                                            ))}
                                        {tab === "group" &&
                                            (groupList.length > 0 ? (
                                                <div className="scrollWrapper">
                                                    <InfiniteScroll
                                                        initialLoad={false}
                                                        loadMore={() => {
                                                            handleLoadSearchDataMore(
                                                                tab
                                                            );
                                                        }}
                                                        hasMore={groupIsHasMore}
                                                        useWindow={false}
                                                    >

                                                        <CommonList
                                                            {...{
                                                                type: "group",
                                                                pikerType: type,
                                                                list: groupList,
                                                                currentList,
                                                                setCurrentList,
                                                                disabledids,
                                                                requiredList,
                                                                multiple,
                                                                isSearchHighlight:
                                                                    true,
                                                                track,
                                                                trackTitle:
                                                                    title,
                                                                isNeedTrack:
                                                                    true,
                                                                isFromSearch:
                                                                    true,
                                                                searchKey:
                                                                    searchKey,
                                                                searchType: tab,
                                                                onClickPartner,
                                                            }}
                                                        />
                                                    </InfiniteScroll>
                                                </div>
                                            ) : (
                                                NoSearchResult
                                            ))}
                                        {/* {tab === "org" && <div>部门list</div>} */}
                                    </TabPane>
                                ))}
                            </Tabs>
                        ) : (
                            NoSearchResult
                        ))}
                </div>
            );
        };

        return (
            <div className={`searchPage ${isSearchMode ? "" : "hide"}`}>
                {searchContent()}
                {searchChildPage && searchChildPageContent()}
            </div>
        );
    }
);

export default SearchPage;
